import {
  asCurrency,
  asPercentage,
  getMonthlyPayment,
  getFixedMortgageAmortization,
  normalizedDate,
  updateValuesFromFields,
  validateFields,
} from '../util'
import { useAppState } from '../Context'
import { EquityBalanceInterestChart, BreakdownChart } from '../components/Charts'
import { AmortizationScheduleTable } from '../components/Tables'
import { InputFields } from '../components/Fields'
import CalculateResults from '../components/CalculateResults'
import CalculateRow from '../components/CalculateRow'
import addMonths from 'date-fns/addMonths'
import differenceInDays from 'date-fns/differenceInDays'
import startOfMonth from 'date-fns/startOfMonth'
import endOfMonth from 'date-fns/endOfMonth'
import format from 'date-fns/format'
import currency from 'currency.js'

function PanelInput() {
  return <InputFields />
}

function PanelCalculate() {
  const { values } = useAppState()

  return (
    <CalculateResults>
      <CalculateRow>
        <div>Principal Amount:</div>
        <div>{asCurrency(values.principal)}</div>
      </CalculateRow>

      <CalculateRow>
        <div>Down Payment Amount:</div>
        <div>{asCurrency(values.downPayment)}</div>
      </CalculateRow>

      <CalculateRow className="font-bold">
        <div>Monthly Payment Amount:</div>
        <div>{asCurrency(values.monthlyPayment)}</div>
      </CalculateRow>

      <CalculateRow>
        <div>Number of Payments:</div>
        <div>{values.numPayments}</div>
      </CalculateRow>

      <CalculateRow>
        <div>Interest Rate:</div>
        <div>{asPercentage(values.interestRate)}</div>
      </CalculateRow>

      <CalculateRow>
        <div>Total Payments:</div>
        <div>{asCurrency(values.amortization.totalPayment)}</div>
      </CalculateRow>

      <CalculateRow>
        <div>Total Interest:</div>
        <div>{asCurrency(values.amortization.totalInterest)}</div>
      </CalculateRow>
    </CalculateResults>
  )
}

function PanelChart() {
  const { values } = useAppState()

  return (
    <div className="py-4 space-y-4">
      <EquityBalanceInterestChart amortization={values.amortization} title="Fixed Rate Payment Calculator" />
      <BreakdownChart amortization={values.amortization} title="Mortgage Payments Breakdown" />
    </div>
  )
}

function PanelDetail() {
  const { values } = useAppState()

  return <AmortizationScheduleTable amortization={values.amortization} />
}

export default {
  name: 'What are my payments for a fixed rate loan?',
  tabs: [
    {
      name: 'Input',
      component: PanelInput,
      beforeTabChange: (dispatch, fields, values) => {
        const validate = validateFields(fields)

        if (validate.isValid) {
          const updatedValues = updateValuesFromFields(values, fields)
          const principal = currency(updatedValues.principal).subtract(updatedValues.downPayment)
          const monthlyPayment = getMonthlyPayment(principal, updatedValues.interestRate, updatedValues.numPayments)
          const dayDiff = differenceInDays(
            endOfMonth(new Date(updatedValues.closingDate)),
            new Date(updatedValues.closingDate)
          )
          const dailyInterest = updatedValues.interestRate / 100 / 365
          const closingInterest = Math.abs(dailyInterest * principal.value * dayDiff)

          const startingEvent = {
            eventNum: 0,
            date: normalizedDate(updatedValues.closingDate),
            formattedDate: format(normalizedDate(updatedValues.closingDate), 'MM-dd-yyyy'),
            payment: currency(closingInterest),
            interest: currency(closingInterest),
            principal: currency(0),
            balance: principal,
            hideFromChart: true,
          }

          const amortization = getFixedMortgageAmortization({
            principal,
            interestRate: updatedValues.interestRate,
            numPayments: updatedValues.numPayments,
            monthlyPayment,
            startDate: updatedValues.dueDate,
            startingEvent,
          })

          dispatch({ type: 'setValues', payload: { ...updatedValues } })
          dispatch({ type: 'setValue', payload: { name: 'monthlyPayment', value: monthlyPayment } })
          dispatch({ type: 'setValue', payload: { name: 'amortization', value: amortization } })
        }

        dispatch({ type: 'setFields', payload: validate.fields })

        return validate.isValid
      },
    },
    {
      name: 'Calculate',
      component: PanelCalculate,
    },
    {
      name: 'Chart',
      component: PanelChart,
    },
    {
      name: 'Detail',
      component: PanelDetail,
    },
  ],
  fields: [
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'principal',
      label: 'Principal Amount',
      value: 100000,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'downPayment',
      label: 'Down Payment Amount',
      value: 0,
    },
    {
      type: 'date',
      name: 'closingDate',
      label: 'Closing Date',
      value: format(new Date(), 'yyyy-MM-dd'),
    },
    {
      type: 'date',
      name: 'dueDate',
      label: 'First Mortgage Payment Due Date',
      value: format(startOfMonth(addMonths(new Date(), 2)), 'yyyy-MM-dd'),
    },
    {
      type: 'number',
      dataType: 'int',
      name: 'numPayments',
      label: 'Number of Payments',
      value: 360,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'interestRate',
      label: 'Interest Rate',
      value: 8.25,
    },
  ],
}
